import { getEnrollmentCheckApi, getWorkflowAssigneesApi } from "@/api/workflowManage";

const program = {
  namespaced: true,
  state: {
    programList: [],
    selectedProgramIdList: [],
    enrolledProgramIds: [],
    enrollmentDataMap: {}, // { [workflowId]: { status, program_steps_executed, program_start_date, program_end_date } }
  },
  getters: {
    selectedProgramList(state) {
      return state.programList.filter(program => state.selectedProgramIdList.includes(program.id));
    },
    activeProgramList(state) {
      return state.programList.filter(p => p.status === 'active');
    },
    enrolledProgramList(state) {
      return state.programList.filter(p => state.enrolledProgramIds.includes(p.id));
    },
  },
  mutations: {
    SET_PROGRAM_LIST(state, programList) {
      state.programList = programList;
    },
    ADD_SELECTED_PROGRAM_ID(state, programId) {
      if (!state.selectedProgramIdList.includes(programId)) {
        state.selectedProgramIdList.push(programId);
      }
    },
    REMOVE_SELECTED_PROGRAM_ID(state, programId) {
      const index = state.selectedProgramIdList.indexOf(programId);
      if (index > -1) {
        state.selectedProgramIdList.splice(index, 1);
      }
    },
    SET_SELECTED_PROGRAM_IDS(state, programIds) {
      state.selectedProgramIdList = programIds;
    },
    CLEAR_SELECTED_PROGRAMS(state) {
      state.selectedProgramIdList = [];
    },
    SET_ENROLLED_PROGRAM_IDS(state, ids) {
      state.enrolledProgramIds = ids;
    },
    SET_ENROLLMENT_DATA_MAP(state, dataMap) {
      state.enrollmentDataMap = dataMap;
    },
    CLEAR_ENROLLED_PROGRAM_IDS(state) {
      state.enrolledProgramIds = [];
      state.enrollmentDataMap = {};
    },
  },
  actions: {
    setProgramList({ commit }, programList) {
      // Normalize program data to ensure price is a number and has required properties
      const normalizedList = Array.isArray(programList) ? programList.map(program => ({
        ...program,
        id: program.id || program.workflow_id || '',
        name: program.name || program.workflow_name || '',
        displayName: program.displayName || program.name || program.workflow_name || '',
        description: program.description || '',
        price: typeof program.price === 'number' ? program.price : Number(program.price) || 0,
        status: program.status || '',
        duration: program.duration || null,
        program_code: program.program_code || null,
        category: 'program',
        // Add sampleTypeSets for OrderSummary compatibility
        sampleTypeSets: {
          blood: new Set(),
          nonBlood: new Set(),
          dryIce: new Set(),
        },
      })) : [];
      commit('SET_PROGRAM_LIST', normalizedList);
    },
    addSelectedProgram({ commit, state }, programId) {
      // Single select for programs - clear others first
      commit('SET_SELECTED_PROGRAM_IDS', []);
      commit('ADD_SELECTED_PROGRAM_ID', programId);
    },
    removeSelectedProgram({ commit }, programId) {
      commit('REMOVE_SELECTED_PROGRAM_ID', programId);
    },
    clearSelectedPrograms({ commit }) {
      commit('CLEAR_SELECTED_PROGRAMS');
    },
    async fetchEnrollmentData({ commit }, patientId) {
      if (!patientId) {
        commit('SET_ENROLLED_PROGRAM_IDS', []);
        commit('SET_ENROLLMENT_DATA_MAP', {});
        return;
      }
      try {
        const response = await getEnrollmentCheckApi(patientId);
        const enrollments = response.data?.enrollments || {};
        const enrolledIds = Object.keys(enrollments).filter(
          id => ['active', 'completed', 'paused', 'pending', 'running'].includes(enrollments[id].status)
        );
        commit('SET_ENROLLED_PROGRAM_IDS', enrolledIds);

        // Fetch assignment data per enrolled workflow to get progress info
        const dataMap = {};
        const assigneePromises = enrolledIds.map(async (workflowId) => {
          try {
            const res = await getWorkflowAssigneesApi(workflowId);
            const assignments = res.data?.assignments || [];
            const programStepsTotal = res.data?.program_steps_total || 0;
            // Find this patient's assignment
            const patientAssignment = assignments.find(
              a => String(a.patient_id) === String(patientId)
            );
            if (patientAssignment) {
              dataMap[workflowId] = {
                status: patientAssignment.status,
                program_steps_executed: patientAssignment.program_steps_executed || 0,
                program_start_date: patientAssignment.program_start_date,
                program_end_date: patientAssignment.program_end_date,
                program_steps_total: programStepsTotal,
              };
            }
          } catch (err) {
            console.error(`Failed to fetch assignees for workflow ${workflowId}:`, err);
          }
        });
        await Promise.all(assigneePromises);
        commit('SET_ENROLLMENT_DATA_MAP', dataMap);
      } catch (error) {
        console.error('Failed to fetch enrollment data:', error);
        commit('SET_ENROLLED_PROGRAM_IDS', []);
        commit('SET_ENROLLMENT_DATA_MAP', {});
      }
    },
  },
};

export default program;
